import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AlertCircle, Link as LinkIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Card, CardContent } from '@/components/ui/card';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { useCreateCustomer } from '@/api/customers/use-customers.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useLeads } from '@/api/leads/use-leads.tsx';
import { useActiveLeadStatuses } from '@/api/lead-statuses/use-lead-statuses';

// Matches the phone format previously enforced by this page's own validateForm().
const CUSTOMER_CREATE_PHONE_REGEX = /^\+?[\d\s-]{10,}$/;

const customerCreateSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().min(1, 'Email is required').email('Invalid email format'),
    phone: z
        .string()
        .optional()
        .refine(value => !value || CUSTOMER_CREATE_PHONE_REGEX.test(value), 'Invalid phone format'),
    lead_status: z.string().min(1, 'Status is required'),
});

type CustomerCreateFormData = z.infer<typeof customerCreateSchema>;

const CustomerCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Fetch active lead statuses for dropdown
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesData?.data || [];

    const [existingLead, setExistingLead] = useState<any>(null);
    const [showLeadLinkOption, setShowLeadLinkOption] = useState(false);

    const createCustomerMutation = useCreateCustomer();

    const form = useForm<CustomerCreateFormData>({
        resolver: zodResolver(customerCreateSchema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            lead_status: leadStatuses[0]?.id || '', // Use first status as default
        },
    });

    const emailValue = form.watch('email');
    const { data: leads } = useLeads({ search: emailValue });

    // Check for existing lead when email changes
    useEffect(() => {
        if (emailValue && emailValue.includes('@') && leads?.data) {
            const matchingLead = leads.data.find(
                lead =>
                    lead.email.toLowerCase() === emailValue.toLowerCase() &&
                    lead.status?.name !== 'converted'
            );
            if (matchingLead) {
                setExistingLead(matchingLead);
                setShowLeadLinkOption(true);
            } else {
                setExistingLead(null);
                setShowLeadLinkOption(false);
            }
        } else {
            setExistingLead(null);
            setShowLeadLinkOption(false);
        }
    }, [emailValue, leads]);

    const handleLinkToLead = async () => {
        if (existingLead) {
            // Create customer and link to existing lead
            const customerData = {
                ...form.getValues(),
                link_to_lead_id: existingLead.id,
            };

            try {
                await createCustomerMutation.mutateAsync(customerData);
                queryClient.invalidateQueries({ queryKey: ['customers'] });
                navigate('/customers');
            } catch (error) {
                console.error('Failed to create customer and link to lead:', error);
            }
        }
    };

    const handleContinueWithoutLink = () => {
        setShowLeadLinkOption(false);
        setExistingLead(null);
    };

    const onSubmit = async (data: CustomerCreateFormData) => {
        try {
            await createCustomerMutation.mutateAsync(data);
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            navigate('/customers');
        } catch (error) {
            console.error('Error creating customer:', error);
        }
    };

    const handleCancel = () => {
        navigate('/customers');
    };

    return (
        <PageContainer>
            <PageHeader
                title="Create New Customer"
                breadcrumbs={[
                    { label: 'Customers', to: '/customers' },
                    { label: 'Create New Customer' },
                ]}
                backTo="/customers"
                backLabel="Back to customers"
            />

            <Card>
                <CardContent className="p-6">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Name<span className="text-destructive">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Email<span className="text-destructive">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <Input type="email" {...field} />
                                            </FormControl>
                                            <FormMessage />

                                            {/* Existing Lead Alert */}
                                            {showLeadLinkOption && existingLead && (
                                                <Alert className="mt-3 border-warning/30 bg-warning/10">
                                                    <AlertCircle className="h-4 w-4 text-warning" />
                                                    <AlertDescription className="text-warning">
                                                        <div className="space-y-2">
                                                            <p className="font-medium">
                                                                A lead with this email already
                                                                exists:
                                                            </p>
                                                            <p className="text-sm">
                                                                <strong>{existingLead.name}</strong>{' '}
                                                                - {existingLead.email}
                                                            </p>
                                                            <div className="flex gap-2 mt-2">
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="warning"
                                                                    onClick={handleLinkToLead}
                                                                    disabled={
                                                                        createCustomerMutation.isPending
                                                                    }
                                                                >
                                                                    <LinkIcon className="h-4 w-4" />
                                                                    {createCustomerMutation.isPending
                                                                        ? 'Creating...'
                                                                        : 'Create Customer & Link to Lead'}
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={
                                                                        handleContinueWithoutLink
                                                                    }
                                                                >
                                                                    Continue Creating Customer
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="phone"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Phone</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="lead_status"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Status<span className="text-destructive">*</span>
                                            </FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select status" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {leadStatuses.map(status => (
                                                        <SelectItem
                                                            key={status.id}
                                                            value={status.id}
                                                        >
                                                            {status.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createCustomerMutation.isPending}>
                                    {createCustomerMutation.isPending ? 'Creating...' : 'Create'}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </PageContainer>
    );
};

export default CustomerCreatePage;
